import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BUG_STATUSES, LEGACY_BUG_STATUSES, ALL_BUG_STATUSES, STATUS_TO_LEGACY,
  BUG_SEVERITIES, statusBadgeClass, priorityBadgeClass, hasPerm
} from "@/lib/constants";
import {
  Plus, Bug as BugIcon, Trash2, ChevronLeft, ChevronRight, X, Search,
  Calendar, RotateCcw, Edit2, MessageSquare, Send, Check
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Pagination from "@/components/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import MemberTagSelector from "@/components/MemberTagSelector";

export default function BugList({ projectId, users = [], defaultBugId, onCloseDetail }) {
  const { user: me } = useAuth();

  // Filters state
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sevFilter, setSevFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Paginated list
  const {
    items: bugs,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    refresh: load,
  } = usePaginatedList("/bugs", {
    project_id: projectId,
    q,
    status: statusFilter === "all" ? "" : statusFilter,
    severity: sevFilter === "all" ? "" : sevFilter,
    assignee_id: assigneeFilter === "all" ? "" : assigneeFilter,
    from_date: fromDate,
    to_date: toDate,
  });

  // Bug Dialogs
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [activity, setActivity] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // Editable title and description in detail
  const [isEditingTitleDesc, setIsEditingTitleDesc] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");

  // Reopen prompt dialog
  const [reopenPromptOpen, setReopenPromptOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  // Form for new bug
  const [form, setForm] = useState({
    title: "",
    description: "",
    severity: "MEDIUM",
    status: "New",
    assignee_id: "",
    tagged_user_ids: [],
    estimated_hours: "",
  });
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  const openedDefaultRef = useRef(null);

  // Auto-open detail if defaultBugId provided
  useEffect(() => {
    if (defaultBugId && openedDefaultRef.current !== defaultBugId) {
      const found = bugs.find((b) => b.id === defaultBugId || b._id === defaultBugId);
      if (found) {
        openedDefaultRef.current = defaultBugId;
        openDetail(found);
      } else {
        api.get(`/bugs/${defaultBugId}`)
          .then((res) => {
            if (res.data) {
              openedDefaultRef.current = defaultBugId;
              openDetail(res.data);
            }
          })
          .catch(() => {});
      }
    }
  }, [defaultBugId, bugs]);

  const handleCloseDetail = () => {
    setDetail(null);
    setActiveImageIndex(null);
    setIsEditingTitleDesc(false);
    if (onCloseDetail) {
      onCloseDetail();
    } else {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has("bugId")) {
          url.searchParams.delete("bugId");
          window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
        }
      } catch {}
    }
  };

  // Handle Incremental File Attachment Selection
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    // Reset file input value so user can select again from any folder
    e.target.value = "";
    if (selectedFiles.length === 0) return;

    const allowedDocs = ["application/pdf", "image/jpeg", "image/png"];
    const allowedVideos = ["video/mp4", "video/quicktime"];

    // Deduplicate against existing files by name and size
    const existingFileKeys = new Set(files.map((f) => `${f.name}-${f.size}`));
    const newUniqueFiles = selectedFiles.filter((f) => !existingFileKeys.has(`${f.name}-${f.size}`));

    if (newUniqueFiles.length === 0) {
      toast.info("Selected file(s) are already attached.");
      return;
    }

    const candidateFiles = [...files, ...newUniqueFiles];

    let docCount = 0;
    let videoCount = 0;

    for (const file of candidateFiles) {
      const isDoc = allowedDocs.includes(file.type);
      const isVideo = allowedVideos.includes(file.type);

      if (!isDoc && !isVideo) {
        toast.error(`Invalid format: ${file.name}. Only PDF, JPG, PNG, MP4, and MOV files are allowed.`);
        return;
      }

      if (isDoc) {
        docCount++;
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`File too large: ${file.name} (Max 5MB for images/PDFs)`);
          return;
        }
      }

      if (isVideo) {
        videoCount++;
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`File too large: ${file.name} (Max 20MB for videos)`);
          return;
        }
      }
    }

    if (docCount > 0 && videoCount > 0) {
      toast.error("Cannot mix images/documents and videos. Upload either 1-10 images/PDFs OR 1 video.");
      return;
    }

    if (docCount > 10) {
      toast.error("You can upload at most 10 images/PDFs.");
      return;
    }

    if (videoCount > 1) {
      toast.error("You can upload at most 1 video.");
      return;
    }

    setFiles(candidateFiles);
    toast.success(`Attached ${newUniqueFiles.length} file(s). Total: ${candidateFiles.length}`);
  };

  const removeSelectedFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const create = async () => {
    const newErrors = {};
    if (!form.title.trim()) {
      newErrors.title = "Bug Title is required";
    }
    if (form.estimated_hours) {
      const est = Number(form.estimated_hours);
      if (isNaN(est) || est <= 0) {
        newErrors.estimated_hours = "Estimated hours must be a positive number";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    setIsUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const { data } = await api.post("/bugs/presigned-url", {
          filename: file.name,
          content_type: file.type,
          file_size: file.size,
        });

        await fetch(data.upload_url, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });

        uploadedUrls.push(data.public_url);
      }

      await api.post("/bugs", {
        ...form,
        project_id: projectId,
        assignee_id: form.assignee_id || null,
        tagged_user_ids: form.tagged_user_ids || [],
        estimated_hours: form.estimated_hours ? Number(form.estimated_hours) : null,
        attachments: uploadedUrls,
      });

      toast.success("Bug reported successfully");
      setOpen(false);
      setForm({
        title: "",
        description: "",
        severity: "MEDIUM",
        status: "New",
        assignee_id: "",
        tagged_user_ids: [],
        estimated_hours: "",
      });
      setErrors({});
      setFiles([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIsUploading(false);
    }
  };

  const openDetail = async (b) => {
    setDetail(b);
    setEditTitle(b.title || "");
    setEditDescription(b.description || "");
    setIsEditingTitleDesc(false);
    setNewComment("");
    const bugId = b?.id || b?._id;
    if (!bugId) return;
    try {
      const [actRes, commRes] = await Promise.all([
        api.get(`/bugs/${bugId}/activity`),
        api.get(`/bugs/${bugId}/comments`).catch(() => ({ data: [] })),
      ]);
      setActivity(actRes.data || []);
      setComments(commRes.data || []);
    } catch {}
  };

  const update = async (patch) => {
    const bugId = detail?.id || detail?._id;
    if (!bugId) return;
    try {
      let res;
      try {
        res = await api.put(`/bugs/${bugId}`, patch);
      } catch (putErr) {
        // Fallback for deployed backend if it only accepts legacy statuses
        if (
          putErr?.response?.status === 400 &&
          typeof putErr?.response?.data?.detail === "string" &&
          putErr.response.data.detail.toLowerCase().includes("invalid status") &&
          patch.status &&
          STATUS_TO_LEGACY[patch.status]
        ) {
          const fallbackStatus = STATUS_TO_LEGACY[patch.status];
          res = await api.put(`/bugs/${bugId}`, { ...patch, status: fallbackStatus });
        } else {
          throw putErr;
        }
      }
      const data = res.data;
      setDetail(data);
      setEditTitle(data.title || "");
      setEditDescription(data.description || "");
      setIsEditingTitleDesc(false);
      const [actRes, commRes] = await Promise.all([
        api.get(`/bugs/${bugId}/activity`),
        api.get(`/bugs/${bugId}/comments`).catch(() => ({ data: [] })),
      ]);
      setActivity(actRes.data || []);
      setComments(commRes.data || []);
      load();
      toast.success("Bug updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === "Reopened" || newStatus === "REOPENED") {
      setReopenReason("");
      setReopenPromptOpen(true);
    } else {
      update({ status: newStatus });
    }
  };

  const submitReopen = async () => {
    if (!reopenReason.trim()) {
      toast.error("Please provide a reason or proof for reopening");
      return;
    }
    await update({ status: "Reopened", reopen_reason: reopenReason.trim() });
    setReopenPromptOpen(false);
    setReopenReason("");
  };

  const saveTitleAndDescription = async () => {
    if (!editTitle.trim()) {
      toast.error("Bug title cannot be empty");
      return;
    }
    await update({ title: editTitle.trim(), description: editDescription });
    setIsEditingTitleDesc(false);
  };

  const handleAddComment = async () => {
    const bugId = detail?.id || detail?._id;
    if (!newComment.trim() || !bugId) return;
    setIsSubmittingComment(true);
    try {
      await api.post(`/bugs/${bugId}/comments`, { content: newComment.trim() });
      setNewComment("");
      const commRes = await api.get(`/bugs/${bugId}/comments`).catch(() => ({ data: [] }));
      setComments(commRes.data || []);
      const actRes = await api.get(`/bugs/${bugId}/activity`);
      setActivity(actRes.data || []);
      toast.success("Comment added");
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Comments feature is deploying. Please restart backend service on server.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const del = async (b) => {
    const bugId = b?.id || b?._id;
    if (!bugId) return;
    if (!window.confirm("Delete bug?")) return;
    try {
      await api.delete(`/bugs/${bugId}`);
      toast.success("Deleted");
      handleCloseDetail();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleEditFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    e.target.value = "";
    if (selectedFiles.length === 0 || !detail) return;

    const allowedDocs = ["application/pdf", "image/jpeg", "image/png"];
    const allowedVideos = ["video/mp4", "video/quicktime"];

    let docCount = getImagesOnly(detail.attachments).length;
    let videoCount = (detail.attachments || []).length - docCount;

    let newDocCount = 0;
    let newVideoCount = 0;

    for (const file of selectedFiles) {
      const isDoc = allowedDocs.includes(file.type);
      const isVideo = allowedVideos.includes(file.type);

      if (!isDoc && !isVideo) {
        toast.error(`Invalid format: ${file.name}. Only PDF, JPG, PNG, MP4, and MOV files are allowed.`);
        return;
      }

      if (isDoc) {
        newDocCount++;
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`File too large: ${file.name} (Max 5MB for images/PDFs)`);
          return;
        }
      }

      if (isVideo) {
        newVideoCount++;
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`File too large: ${file.name} (Max 20MB for videos)`);
          return;
        }
      }
    }

    if ((docCount > 0 || newDocCount > 0) && (videoCount > 0 || newVideoCount > 0)) {
      toast.error("Cannot mix images/documents and videos. A bug can only have either images/PDFs OR 1 video.");
      return;
    }

    if (docCount + newDocCount > 10) {
      toast.error(`Limit exceeded. You can have at most 10 images/PDFs. (Current: ${docCount}, selected: ${newDocCount})`);
      return;
    }

    if (videoCount + newVideoCount > 1) {
      toast.error(`Limit exceeded. You can have at most 1 video.`);
      return;
    }

    setIsUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of selectedFiles) {
        const { data } = await api.post("/bugs/presigned-url", {
          filename: file.name,
          content_type: file.type,
          file_size: file.size,
        });

        await fetch(data.upload_url, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });

        uploadedUrls.push(data.public_url);
      }

      const updatedAttachments = [...(detail.attachments || []), ...uploadedUrls];
      await update({ attachments: updatedAttachments });
      toast.success("Attachment added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveAttachment = async (urlToRemove) => {
    if (!detail) return;
    if (!window.confirm("Remove this attachment?")) return;
    try {
      const updatedAttachments = (detail.attachments || []).filter((url) => url !== urlToRemove);
      await update({ attachments: updatedAttachments });
      toast.success("Attachment removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const userName = (id) => users.find((u) => u.id === id)?.name || "Unassigned";

  const getImagesOnly = (attachments) => {
    return (attachments || []).filter((url) => {
      const lowercaseUrl = url.toLowerCase();
      const isVideo =
        lowercaseUrl.endsWith(".mp4") || lowercaseUrl.endsWith(".mov") || lowercaseUrl.endsWith(".quicktime");
      const isPdf = lowercaseUrl.endsWith(".pdf");
      return !isVideo && !isPdf;
    });
  };

  const resetFilters = () => {
    setQ("");
    setStatusFilter("all");
    setSevFilter("all");
    setAssigneeFilter("all");
    setFromDate("");
    setToDate("");
  };

  const canCreate = hasPerm(me, "bug.create");

  return (
    <div className="space-y-4" data-testid="bug-list">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BugIcon className="w-5 h-5 text-red-500" />
          <span className="font-semibold text-foreground">Project Bugs</span>
          <Badge variant="secondary" className="font-mono text-xs">
            {total} total
          </Badge>
        </div>
        {canCreate && projectId && (
          <Button
            size="sm"
            onClick={() => {
              setOpen(true);
              setErrors({});
              setFiles([]);
            }}
            data-testid="new-bug-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Report Bug
          </Button>
        )}
      </div>

      {/* Filter and Search Toolbar */}
      <div className="bg-card border border-border p-3 rounded-lg shadow-sm space-y-3">
        <div className="flex flex-wrap gap-2.5 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, description, or ID..."
              className="pl-9 h-9 text-xs"
              data-testid="bug-search-input"
            />
          </div>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="bug-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {BUG_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
              {LEGACY_BUG_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s} (Legacy)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Severity Filter */}
          <Select value={sevFilter} onValueChange={setSevFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="bug-filter-severity">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              {BUG_SEVERITIES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Assignee Filter */}
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="bug-filter-assignee">
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignees</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Reset button */}
          {(q || statusFilter !== "all" || sevFilter !== "all" || assigneeFilter !== "all" || fromDate || toDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground"
              title="Reset filters"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>

        {/* Date Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-border/40 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <span>Reported Date:</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">From:</span>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-36 h-8 text-xs font-mono"
              data-testid="bug-filter-from-date"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">To:</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-36 h-8 text-xs font-mono"
              data-testid="bug-filter-to-date"
            />
          </div>
        </div>
      </div>

      {/* Bugs Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-3 py-3 font-semibold w-12 text-center">S.No</th>
                <th className="px-3 py-3 font-semibold w-24">Bug ID</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-3 py-3 font-semibold">Severity</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Assignee & Tagged</th>
                <th className="px-3 py-3 font-semibold text-center">Est (h)</th>
                <th className="px-3 py-3 font-semibold text-center">Reopens</th>
                <th className="px-3 py-3 font-semibold">Reported</th>
              </tr>
            </thead>
            <tbody>
              {bugs.map((b, idx) => (
                <tr
                  key={b.id}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => openDetail(b)}
                  data-testid={`bug-row-${b.id}`}
                >
                  <td className="px-3 py-3 font-mono text-xs text-muted-foreground text-center">
                    {(page - 1) * pageSize + idx + 1}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs font-semibold text-primary">
                    #b-{b.id ? b.id.slice(-6) : ""}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    <div>{b.title}</div>
                    {b.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5 font-normal">
                        {b.description}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className={priorityBadgeClass(b.severity)}>
                      {b.severity}
                    </Badge>
                  </td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className={statusBadgeClass(b.status)}>
                      {b.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-medium text-foreground">{userName(b.assignee_id)}</div>
                    {b.tagged_user_ids && b.tagged_user_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {b.tagged_user_ids.map((uid) => (
                          <Badge
                            key={uid}
                            variant="secondary"
                            className="text-[10px] py-0 px-1.5 font-normal bg-secondary/70 text-secondary-foreground"
                          >
                            @{userName(uid).split(" ")[0]}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-center">{b.estimated_hours ?? "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-center">
                    {b.reopen_count > 0 ? (
                      <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                        {b.reopen_count}×
                      </Badge>
                    ) : (
                      "0"
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground font-mono">
                    {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
              {bugs.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">
                    No bugs match the current filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="bug-list-pagination"
        />
      </Card>

      {/* Report Bug Dialog */}
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          setErrors({});
          if (!isOpen) setFiles([]);
        }}
      >
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="bug-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BugIcon className="w-5 h-5 text-red-500" /> Report Bug
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 font-sans text-sm">
            <div>
              <Label>Bug Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => {
                  setForm({ ...form, title: e.target.value });
                  setErrors((prev) => ({ ...prev, title: "" }));
                }}
                data-testid="bug-form-title"
                disabled={isUploading}
                placeholder="Brief summary of the defect"
              />
              {errors.title && <span className="text-xs text-red-500 block mt-1">{errors.title}</span>}
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="bug-form-description"
                disabled={isUploading}
                rows={3}
                placeholder="Steps to reproduce, expected vs actual behavior..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Severity</Label>
                <Select
                  value={form.severity}
                  onValueChange={(v) => setForm({ ...form, severity: v })}
                  disabled={isUploading}
                >
                  <SelectTrigger data-testid="bug-form-severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUG_SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                  disabled={isUploading}
                >
                  <SelectTrigger data-testid="bug-form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUG_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Primary Assignee</Label>
                <Select
                  value={form.assignee_id || "none"}
                  onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? "" : v })}
                  disabled={isUploading}
                >
                  <SelectTrigger data-testid="bug-form-assignee">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Estimated Hours</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.estimated_hours}
                  onChange={(e) => {
                    setForm({ ...form, estimated_hours: e.target.value });
                    setErrors((prev) => ({ ...prev, estimated_hours: "" }));
                  }}
                  data-testid="bug-form-estimate"
                  disabled={isUploading}
                  placeholder="e.g. 2.5"
                />
                {errors.estimated_hours && (
                  <span className="text-xs text-red-500 block mt-1">{errors.estimated_hours}</span>
                )}
              </div>
            </div>

            {/* Member Tagging */}
            <div className="pt-1">
              <MemberTagSelector
                users={users}
                selectedUserIds={form.tagged_user_ids}
                onChange={(taggedIds) => setForm({ ...form, tagged_user_ids: taggedIds })}
                disabled={isUploading}
                label="Tag Team Members (Collaborators / Reviewers)"
                placeholder="Tag another team member..."
              />
            </div>

            {/* Incremental File Attachments */}
            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold">
                  Attachments (PDF, JPG, PNG ≤ 5MB, MP4, MOV ≤ 20MB)
                </Label>
                <span className="text-[11px] text-muted-foreground font-mono">{files.length} selected</span>
              </div>

              <Input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov"
                onChange={handleFileChange}
                data-testid="bug-form-attachments"
                disabled={isUploading}
                className="text-xs"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Attach 1-10 images/PDFs OR 1 video. You can select multiple times from different folders to add more.
              </p>

              {files.length > 0 && (
                <div className="mt-2.5 max-h-36 overflow-y-auto border border-border rounded-md p-2 bg-muted/20 space-y-1.5">
                  <div className="text-xs font-semibold text-foreground mb-1">Files to upload:</div>
                  {files.map((f, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-1 px-2 bg-background border border-border rounded text-xs"
                    >
                      <div className="flex items-center gap-1.5 truncate max-w-[340px]">
                        <span className="font-mono text-[10px] text-muted-foreground">#{idx + 1}</span>
                        <span className="truncate">{f.name}</span>
                        <span className="text-[10px] text-muted-foreground font-mono">
                          ({(f.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSelectedFile(idx)}
                        disabled={isUploading}
                        className="text-red-500 hover:text-red-700 p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        title="Remove file"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                setFiles([]);
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button onClick={create} data-testid="bug-form-save" disabled={isUploading}>
              {isUploading ? "Uploading..." : "Report Bug"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bug Detail Dialog */}
      <Dialog
        open={!!detail}
        onOpenChange={(v) => {
          if (!v) {
            handleCloseDetail();
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bug-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs font-semibold text-primary">#b-{detail.id.slice(-6)}</span>
                      <Badge variant="outline" className={statusBadgeClass(detail.status)}>
                        {detail.status}
                      </Badge>
                      <Badge variant="outline" className={priorityBadgeClass(detail.severity)}>
                        {detail.severity}
                      </Badge>
                      {(detail.reopen_count || 0) > 0 && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-purple-50 text-purple-800 border-purple-200"
                        >
                          Reopened {detail.reopen_count}×
                        </Badge>
                      )}
                      {(detail.reassign_count || 0) > 0 && (
                        <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-800 border-sky-200">
                          Reassigned {detail.reassign_count}×
                        </Badge>
                      )}
                    </div>

                    {isEditingTitleDesc ? (
                      <div className="space-y-2 mt-2">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="font-bold text-base h-9"
                          placeholder="Bug title"
                          data-testid="bug-edit-title-input"
                        />
                        <Textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className="text-xs"
                          rows={4}
                          placeholder="Bug description"
                          data-testid="bug-edit-description-input"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveTitleAndDescription} className="h-7 text-xs">
                            <Check className="w-3.5 h-3.5 mr-1" /> Save Title & Description
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setIsEditingTitleDesc(false);
                              setEditTitle(detail.title || "");
                              setEditDescription(detail.description || "");
                            }}
                            className="h-7 text-xs"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="group relative">
                        <DialogTitle className="text-xl font-heading font-bold text-foreground pr-8">
                          {detail.title}
                        </DialogTitle>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-2">
                          {detail.description || "No description provided."}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsEditingTitleDesc(true)}
                          className="mt-2 h-7 text-xs text-muted-foreground hover:text-foreground"
                          data-testid="bug-edit-title-desc-btn"
                        >
                          <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit Title & Description
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-5 pt-3">
                {/* Meta Attributes Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-muted/20 p-3 rounded-lg border border-border">
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select value={detail.status} onValueChange={handleStatusChange}>
                      <SelectTrigger className="h-8 text-xs mt-1" data-testid="bug-detail-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {detail.status && !BUG_STATUSES.includes(detail.status) && (
                          <SelectItem value={detail.status}>
                            {detail.status} (Legacy)
                          </SelectItem>
                        )}
                        {BUG_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Severity</Label>
                    <Select value={detail.severity} onValueChange={(v) => update({ severity: v })}>
                      <SelectTrigger className="h-8 text-xs mt-1" data-testid="bug-detail-severity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUG_SEVERITIES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Primary Assignee</Label>
                    <Select
                      value={detail.assignee_id || "none"}
                      onValueChange={(v) => update({ assignee_id: v === "none" ? null : v })}
                    >
                      <SelectTrigger className="h-8 text-xs mt-1" data-testid="bug-detail-assignee">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unassigned</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Est Hours</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={detail.estimated_hours ?? ""}
                      onChange={(e) =>
                        update({ estimated_hours: e.target.value ? Number(e.target.value) : null })
                      }
                      className="h-8 text-xs font-mono mt-1"
                      data-testid="bug-detail-estimate"
                    />
                  </div>
                </div>

                {/* Tagged Members in Detail */}
                <div className="p-3 border border-border rounded-lg bg-card">
                  <MemberTagSelector
                    users={users}
                    selectedUserIds={detail.tagged_user_ids || []}
                    onChange={(newIds) => update({ tagged_user_ids: newIds })}
                    label="Tagged Members"
                    placeholder="Tag another member to this bug..."
                  />
                </div>

                {/* Attachments Section */}
                <div className="border border-border rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold">Attachments ({(detail.attachments || []).length})</Label>
                    {hasPerm(me, "bug.create") && (
                      <label className="cursor-pointer text-xs text-primary hover:underline flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Add Attachments
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.mp4,.mov"
                          onChange={handleEditFileChange}
                          className="hidden"
                          disabled={isUploading}
                        />
                      </label>
                    )}
                  </div>

                  {detail.attachments && detail.attachments.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {detail.attachments.map((url, idx) => {
                        const lowercaseUrl = url.toLowerCase();
                        const isVideo =
                          lowercaseUrl.endsWith(".mp4") ||
                          lowercaseUrl.endsWith(".mov") ||
                          lowercaseUrl.endsWith(".quicktime");
                        const isPdf = lowercaseUrl.endsWith(".pdf");

                        if (isVideo) {
                          return (
                            <div
                              key={idx}
                              className="col-span-full border border-border rounded p-2 bg-muted/20 relative group"
                            >
                              {hasPerm(me, "bug.create") && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveAttachment(url);
                                  }}
                                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                  title="Remove video"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <video src={url} controls className="w-full max-h-60 rounded" />
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary underline mt-1 block"
                              >
                                Open Video in new tab
                              </a>
                            </div>
                          );
                        } else if (isPdf) {
                          return (
                            <div
                              key={idx}
                              className="border border-border rounded p-2.5 flex items-center justify-between bg-muted/20 col-span-full sm:col-span-1 relative group"
                            >
                              <span className="text-xs truncate font-mono">PDF Attachment</span>
                              <div className="flex items-center gap-2">
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary underline"
                                >
                                  View
                                </a>
                                {hasPerm(me, "bug.create") && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveAttachment(url);
                                    }}
                                    className="text-red-500 hover:text-red-700 p-0.5"
                                    title="Remove PDF"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        } else {
                          const imagesList = getImagesOnly(detail.attachments);
                          const imgIdx = imagesList.indexOf(url);
                          return (
                            <div
                              key={idx}
                              className="border border-border rounded p-2 bg-muted/20 flex flex-col items-center cursor-zoom-in hover:bg-muted/40 transition-colors relative group"
                              onClick={() => setActiveImageIndex(imgIdx)}
                            >
                              {hasPerm(me, "bug.create") && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveAttachment(url);
                                  }}
                                  className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                  title="Remove image"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                              <img src={url} alt="Attachment" className="max-h-24 object-contain rounded mb-1" />
                              <span className="text-[9px] text-muted-foreground mt-1 self-start">Click to Expand</span>
                            </div>
                          );
                        }
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">No attachments for this bug.</div>
                  )}
                </div>

                {/* Comments Section */}
                <div className="border border-border rounded-lg p-3 space-y-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                    <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>Comments ({comments.length})</span>
                  </div>

                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {comments.map((c) => (
                      <div key={c.id} className="p-2.5 rounded bg-muted/20 border border-border text-xs space-y-1">
                        <div className="flex items-center justify-between text-muted-foreground">
                          <span className="font-semibold text-foreground">{c.user_name || c.author_name || "Unknown"}</span>
                          <span className="font-mono text-[10px]">
                            {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
                          </span>
                        </div>
                        <div className="text-foreground whitespace-pre-wrap">{c.content}</div>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <div className="text-xs text-muted-foreground italic py-1">No comments yet.</div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-1">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment or update note..."
                      className="h-8 text-xs"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                      disabled={isSubmittingComment}
                    />
                    <Button
                      size="sm"
                      onClick={handleAddComment}
                      disabled={isSubmittingComment || !newComment.trim()}
                      className="h-8 text-xs shrink-0"
                    >
                      <Send className="w-3 h-3 mr-1" /> Post
                    </Button>
                  </div>
                </div>

                {/* Activity Log */}
                <div className="border border-border rounded-lg p-3 space-y-2">
                  <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                    Activity History
                  </div>
                  <div className="space-y-1 text-xs max-h-36 overflow-y-auto" data-testid="bug-activity-log">
                    {activity.map((a) => (
                      <div
                        key={a.id}
                        className="flex justify-between text-muted-foreground font-mono border-b border-border/40 py-1"
                      >
                        <span className="font-semibold text-foreground">
                          {a.event} {a.details?.from ? `${a.details.from} → ${a.details.to || ""}` : ""}
                        </span>
                        <span>{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                    {activity.length === 0 && (
                      <div className="text-muted-foreground text-xs italic">No activity recorded.</div>
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-4 flex items-center justify-between">
                <div>
                  {hasPerm(me, "bug.delete") && (
                    <Button variant="ghost" size="sm" onClick={() => del(detail)} data-testid="bug-detail-delete" className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete Bug
                    </Button>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={handleCloseDetail}
                >
                  Close
                </Button>
              </DialogFooter>

              {/* Lightbox */}
              {activeImageIndex !== null && (
                <div
                  className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center select-none"
                  data-testid="image-lightbox"
                >
                  <button
                    onClick={() => setActiveImageIndex(null)}
                    type="button"
                    className="absolute top-4 right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer"
                    aria-label="Close Lightbox"
                  >
                    <X className="w-7 h-7" />
                  </button>

                  {getImagesOnly(detail.attachments).length > 1 && (
                    <button
                      onClick={() => {
                        const images = getImagesOnly(detail.attachments);
                        setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
                      }}
                      type="button"
                      className="absolute left-4 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer"
                      aria-label="Previous Image"
                    >
                      <ChevronLeft className="w-8 h-8" />
                    </button>
                  )}

                  <div className="flex flex-col items-center max-w-[85vw] max-h-[80vh]">
                    <img
                      src={getImagesOnly(detail.attachments)[activeImageIndex]}
                      alt="Fullscreen Preview"
                      className="max-w-full max-h-[75vh] object-contain rounded-md shadow-2xl transition-all"
                    />
                    <div className="text-white/60 text-xs mt-4 flex items-center gap-4">
                      <span>
                        {activeImageIndex + 1} of {getImagesOnly(detail.attachments).length}
                      </span>
                      <span>•</span>
                      <a
                        href={getImagesOnly(detail.attachments)[activeImageIndex]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline hover:text-primary-foreground font-semibold"
                      >
                        Open Original File
                      </a>
                    </div>
                  </div>

                  {getImagesOnly(detail.attachments).length > 1 && (
                    <button
                      onClick={() => {
                        const images = getImagesOnly(detail.attachments);
                        setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
                      }}
                      type="button"
                      className="absolute right-4 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer"
                      aria-label="Next Image"
                    >
                      <ChevronRight className="w-8 h-8" />
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reopen Reason Dialog */}
      <Dialog open={reopenPromptOpen} onOpenChange={setReopenPromptOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <RotateCcw className="w-5 h-5" /> Reopen Bug Ticket
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">
              Please specify the reason or attach verification details for reopening this bug. This reason will be
              automatically logged into the bug activity and comments.
            </p>
            <div>
              <Label className="text-xs font-semibold">Reason for Reopening *</Label>
              <Textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g. Issue still reproduces on build v1.0.4 when..."
                rows={4}
                className="text-xs mt-1"
                data-testid="reopen-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenPromptOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitReopen} className="bg-purple-600 hover:bg-purple-700 text-white">
              Confirm Reopen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
